export interface ServiceCategory {
  id: string
  name: string
  slug: string
  parentId?: string | null
}

export interface Service {
  /** URL segment used by the `/services/:slug` route */
  slug: string
  title: string
  /** Short blurb shown on the services listing cards */
  description: string
  /** Listing card icon */
  icon: string
  /** Hero image on the detail page */
  image: string
  /** Two supporting images on the detail page */
  gallery: [string, string]
  /** Count shown in the detail page category sidebar */
  count: number
  /** Intro paragraphs on the detail page */
  overview: string[]
  /** "The benefits of …" checklist */
  benefits: string[]
  /** Shared website categories assigned in the dashboard taxonomy */
  categories?: ServiceCategory[]
  /** SEO metadata authored in the dashboard service screen */
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  ogImage?: string
  canonicalUrl?: string
  noIndex?: boolean
}

const SERVICE_IMAGES = {
  image: '/assets/images/services/service-details/service-single-1.jpg',
  gallery: [
    '/assets/images/services/service-details/service-single-gallery-1.jpg',
    '/assets/images/services/service-details/service-single-gallery-2.jpg',
  ] as [string, string],
}

/**
 * Single source of truth for the six core medical services offered at Fast Aid
 * Hospital, Chauddagram. The listing page and the `/services/:slug` detail
 * pages are both generated from this list.
 */
export const services: Service[] = [
  {
    slug: 'emergency-care',
    title: 'Emergency & Trauma Care',
    description:
      'Round-the-clock emergency and trauma care with rapid triage, resuscitation and critical-care support.',
    icon: '/assets/images/services/icon-service-1.png',
    count: 24,
    overview: [
      'The Emergency Department at Fast Aid Hospital, Chauddagram is open 24 hours a day, 7 days a week to treat accidents, injuries, sudden illness and life-threatening emergencies. Our emergency physicians, nurses and technicians are trained to act within minutes of your arrival.',
      'From road-traffic accidents and fractures to chest pain, breathing difficulty, poisoning and very high fever, we provide immediate assessment, stabilisation and, when needed, admission or referral to a higher centre — with clear documentation and regular updates for the family.',
    ],
    benefits: [
      '24/7 emergency physicians and trained nursing staff',
      'Rapid triage and resuscitation bay for critical patients',
      'Ambulance and patient-transport support',
      'On-site laboratory, X-ray and ECG for fast decisions',
      'Clear guidance and referral when specialist care is required',
    ],
    ...SERVICE_IMAGES,
  },
  {
    slug: 'general-medicine',
    title: 'General Medicine & Outdoor',
    description:
      'Everyday outpatient care for fever, infection, diabetes, blood pressure and general health concerns.',
    icon: '/assets/images/services/icon-service-2.png',
    count: 15,
    overview: [
      'Our General Medicine (outdoor) service is the first point of contact for most patients at Fast Aid Hospital. Whether you have a persistent fever, cough, gastric problem, weakness, diabetes or high blood pressure, our physicians take the time to examine you and explain your condition clearly.',
      'We combine careful history-taking with on-site diagnostic support, so most patients receive a diagnosis and a treatment plan in a single visit. Long-term conditions are followed up with regular reviews, medicine adjustment and lifestyle counselling.',
    ],
    benefits: [
      'Experienced physicians for adults of all ages',
      'Same-day diagnosis with on-site laboratory and imaging',
      'Chronic care for diabetes, hypertension and asthma',
      'Health counselling, diet and lifestyle guidance',
      'Easy follow-up and prescription review',
    ],
    ...SERVICE_IMAGES,
  },
  {
    slug: 'cardiology',
    title: 'Cardiology',
    description:
      'Heart care including ECG, blood-pressure management, chest-pain evaluation and cardiac risk assessment.',
    icon: '/assets/images/services/icon-service-3.png',
    count: 12,
    overview: [
      'Heart disease is one of the leading causes of death in Bangladesh, and many cases can be prevented or controlled when they are detected early. Our cardiology service evaluates chest pain, palpitations, breathlessness, high blood pressure and irregular heart rhythms.',
      'We provide ECG, cardiac risk profiling and blood-pressure monitoring, then build a treatment plan that may include medication, lifestyle changes and referral for advanced procedures such as angiography or angioplasty when they are required.',
    ],
    benefits: [
      'ECG and cardiac risk assessment on site',
      'Management of hypertension and high cholesterol',
      'Chest-pain and palpitation evaluation',
      'Post-heart-attack follow-up and medication review',
      'Clear referral pathway for advanced cardiac procedures',
    ],
    ...SERVICE_IMAGES,
  },
  {
    slug: 'orthopedics',
    title: 'Orthopedics & Trauma',
    description:
      'Care for fractures, joint pain, sports injuries and musculoskeletal problems for all ages.',
    icon: '/assets/images/services/icon-service-4.png',
    count: 14,
    overview: [
      'Our orthopedics and trauma service treats bone fractures, dislocations, sprains, back and joint pain, and sports injuries. The team works closely with the emergency department to stabilise accident patients quickly and safely.',
      'Treatment ranges from plaster casting, splinting and pain management to minor surgical procedures. We also provide physiotherapy guidance and rehabilitation advice so patients regain movement and strength as fully as possible.',
    ],
    benefits: [
      'Fracture reduction, casting and splinting',
      'Joint, back and knee pain assessment',
      'Sports-injury and accident care',
      'Minor orthopedic procedures under safe anaesthesia',
      'Physiotherapy and rehabilitation guidance',
    ],
    ...SERVICE_IMAGES,
  },
  {
    slug: 'pediatrics',
    title: 'Pediatrics & Child Health',
    description:
      'Gentle, child-focused care for fever, infection, nutrition, growth and vaccination needs.',
    icon: '/assets/images/services/icon-service-5.png',
    count: 18,
    overview: [
      'Children need care designed around them. At Fast Aid Hospital, our pediatrics service handles newborn checks, childhood fever, diarrhoea, respiratory infection, malnutrition, growth concerns and routine immunisation.',
      'We use a calm, child-friendly approach so young patients feel safe, and we give parents clear advice on home care, danger signs and when to return. Immunisation reminders and growth monitoring help every child stay on track.',
    ],
    benefits: [
      'Child-friendly examination and counselling',
      'Fever, diarrhoea and respiratory infection care',
      'Growth, nutrition and development assessment',
      'Routine immunisation and vaccination advice',
      'Clear guidance for parents on home care and danger signs',
    ],
    ...SERVICE_IMAGES,
  },
  {
    slug: 'diagnostics-imaging',
    title: 'Diagnostic Laboratory & Imaging',
    description:
      'Accurate pathology, ECG, ultrasound and X-ray services that support every department in the hospital.',
    icon: '/assets/images/services/icon-service-6.png',
    count: 20,
    overview: [
      'Accurate diagnosis is the foundation of good treatment. Our diagnostic centre offers pathology tests, ECG, ultrasound and X-ray under one roof, with strict quality control and same-day reporting for most routine tests.',
      'Results are shared securely with your doctor and can be collected from the reception or viewed through the patient portal. Our laboratory follows standard operating procedures to ensure reliable, repeatable results you and your physician can trust.',
    ],
    benefits: [
      'Wide range of blood, urine and pathology tests',
      'ECG, ultrasound and digital X-ray services',
      'Same-day reporting for most routine tests',
      'Strict quality control and safe sample handling',
      'Reports available at reception and on the patient portal',
    ],
    ...SERVICE_IMAGES,
  },
]

export function getService(slug: string | undefined): Service | undefined {
  if (!slug) return undefined
  return services.find((service) => service.slug === slug)
}
